import React, { useMemo } from "react";
import { normalizeEdenVisualizations } from "../eden/core/edenVisualizationValidator";
import "./EdenChatVisualizationRenderer.css";

const CHART_COLORS = ["#8fb4ff", "#c77dff", "#4dd4ac", "#f2b35f", "#f06f9c", "#7bdff2"];
const SVG_WIDTH = 420;
const SVG_HEIGHT = 220;
const PADDING = { top: 18, right: 22, bottom: 38, left: 48 };

function formatValue(value, format) {
  if (value == null || value === "") return "N/A";
  if (typeof value === "string") return value;
  const number = Number(value);
  if (!Number.isFinite(number)) return String(value);

  if (format === "currency") {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: Math.abs(number) >= 1000 ? 0 : 2,
    }).format(number);
  }
  if (format === "percent") {
    return `${number.toFixed(Math.abs(number) >= 10 ? 0 : 1)}%`;
  }
  if (format === "integer") {
    return new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(number);
  }
  if (format === "compact") {
    return new Intl.NumberFormat("en-US", {
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(number);
  }
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(number);
}

function numericValues(spec) {
  return (spec.data || []).flatMap((row) =>
    (spec.series || []).map((item) => Number(row[item.key])).filter(Number.isFinite)
  );
}

function buildScale(values) {
  const minValue = Math.min(0, ...values);
  const maxValue = Math.max(0, ...values);
  const span = maxValue - minValue || 1;
  const x0 = PADDING.left;
  const y0 = SVG_HEIGHT - PADDING.bottom;
  const chartWidth = SVG_WIDTH - PADDING.left - PADDING.right;
  const chartHeight = SVG_HEIGHT - PADDING.top - PADDING.bottom;

  return {
    minValue,
    maxValue,
    x0,
    y0,
    chartWidth,
    chartHeight,
    xAt(index, count) {
      if (count <= 1) return x0 + chartWidth / 2;
      return x0 + (chartWidth * index) / (count - 1);
    },
    yAt(value) {
      const number = Number(value);
      const ratio = (Number.isFinite(number) ? number - minValue : 0) / span;
      return y0 - ratio * chartHeight;
    },
  };
}

function AxisLabels({ spec, scale }) {
  const firstRow = spec.data?.[0] || {};
  const lastRow = spec.data?.[spec.data.length - 1] || {};
  const firstLabel = String(firstRow[spec.xKey] ?? "");
  const lastLabel = String(lastRow[spec.xKey] ?? "");
  const firstSeries = spec.series?.[0] || {};

  return (
    <>
      <line className="eden-chat-viz__axis" x1={scale.x0} x2={scale.x0 + scale.chartWidth} y1={scale.y0} y2={scale.y0} />
      <line className="eden-chat-viz__axis" x1={scale.x0} x2={scale.x0} y1={PADDING.top} y2={scale.y0} />
      <text className="eden-chat-viz__axis-label" x={scale.x0} y={SVG_HEIGHT - 12} textAnchor="start">
        {firstLabel}
      </text>
      <text className="eden-chat-viz__axis-label" x={scale.x0 + scale.chartWidth} y={SVG_HEIGHT - 12} textAnchor="end">
        {lastLabel || firstLabel}
      </text>
      <text className="eden-chat-viz__axis-label" x={8} y={PADDING.top + 8}>
        {formatValue(scale.maxValue, firstSeries.format)}
      </text>
      <text className="eden-chat-viz__axis-label" x={8} y={scale.y0}>
        {formatValue(scale.minValue, firstSeries.format)}
      </text>
    </>
  );
}

function LineChartSpec({ spec }) {
  const values = numericValues(spec);
  const scale = buildScale(values);
  const count = spec.data.length;

  return (
    <svg
      className="eden-chat-viz__svg"
      viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`}
      role="img"
      aria-label={`${spec.title} line chart`}
    >
      <AxisLabels spec={spec} scale={scale} />
      {(spec.series || []).map((series, seriesIndex) => {
        const points = spec.data
          .map((row, index) => {
            const value = Number(row[series.key]);
            if (!Number.isFinite(value)) return null;
            return `${scale.xAt(index, count)},${scale.yAt(value)}`;
          })
          .filter(Boolean);
        return (
          <g key={series.key}>
            <polyline
              className="eden-chat-viz__line"
              points={points.join(" ")}
              fill="none"
              stroke={CHART_COLORS[seriesIndex % CHART_COLORS.length]}
            />
            {spec.data.map((row, index) => {
              const value = Number(row[series.key]);
              if (!Number.isFinite(value)) return null;
              return (
                <circle
                  key={`${series.key}-${index}`}
                  className="eden-chat-viz__point"
                  cx={scale.xAt(index, count)}
                  cy={scale.yAt(value)}
                  r="3.5"
                  fill={CHART_COLORS[seriesIndex % CHART_COLORS.length]}
                />
              );
            })}
          </g>
        );
      })}
    </svg>
  );
}

function BarChartSpec({ spec }) {
  const values = numericValues(spec);
  const scale = buildScale(values);
  const groups = spec.data.length || 1;
  const seriesCount = spec.series.length || 1;
  const groupGap = 12;
  const availableGroupWidth = scale.chartWidth / groups;
  const barWidth = Math.max(8, Math.min(28, (availableGroupWidth - groupGap) / seriesCount));

  return (
    <svg
      className="eden-chat-viz__svg"
      viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`}
      role="img"
      aria-label={`${spec.title} bar chart`}
    >
      <AxisLabels spec={spec} scale={scale} />
      {spec.data.map((row, rowIndex) => {
        const groupStart = scale.x0 + rowIndex * availableGroupWidth + groupGap / 2;
        const labelX = groupStart + (barWidth * seriesCount) / 2;
        return (
          <g key={`${spec.xKey}-${rowIndex}`}>
            {spec.series.map((series, seriesIndex) => {
              const value = Number(row[series.key]);
              if (!Number.isFinite(value)) return null;
              const y = scale.yAt(value);
              const height = Math.max(1, scale.y0 - y);
              return (
                <rect
                  key={series.key}
                  className="eden-chat-viz__bar"
                  x={groupStart + seriesIndex * barWidth}
                  y={y}
                  width={barWidth * 0.82}
                  height={height}
                  rx="3"
                  fill={CHART_COLORS[seriesIndex % CHART_COLORS.length]}
                />
              );
            })}
            <text className="eden-chat-viz__axis-label" x={labelX} y={SVG_HEIGHT - 12} textAnchor="middle">
              {String(row[spec.xKey] ?? "").slice(0, 14)}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function PieChartSpec({ spec }) {
  const series = spec.series[0];
  const values = spec.data
    .map((row) => ({
      label: String(row[spec.categoryKey] ?? ""),
      value: Math.max(0, Number(row[series.key]) || 0),
    }))
    .filter((item) => item.value > 0);
  const total = values.reduce((sum, item) => sum + item.value, 0);
  const radius = 68;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;

  return (
    <div className="eden-chat-viz__pie-layout" role="img" aria-label={`${spec.title} pie chart`}>
      <svg className="eden-chat-viz__pie" viewBox="0 0 180 180">
        <circle className="eden-chat-viz__pie-track" cx="90" cy="90" r={radius} fill="none" />
        {values.map((item, index) => {
          const share = total > 0 ? item.value / total : 0;
          const dash = share * circumference;
          const segment = (
            <circle
              key={item.label}
              className="eden-chat-viz__pie-segment"
              cx="90"
              cy="90"
              r={radius}
              fill="none"
              stroke={CHART_COLORS[index % CHART_COLORS.length]}
              strokeDasharray={`${dash} ${circumference - dash}`}
              strokeDashoffset={-offset}
            />
          );
          offset += dash;
          return segment;
        })}
        <text className="eden-chat-viz__pie-total" x="90" y="86" textAnchor="middle">
          {formatValue(total, series.format)}
        </text>
        <text className="eden-chat-viz__pie-caption" x="90" y="106" textAnchor="middle">
          total
        </text>
      </svg>
      <div className="eden-chat-viz__legend">
        {values.map((item, index) => (
          <div className="eden-chat-viz__legend-row" key={item.label}>
            <span className={`eden-chat-viz__legend-swatch eden-chat-viz__legend-swatch--${index % CHART_COLORS.length}`} />
            <span>{item.label}</span>
            <strong>{formatValue(item.value, series.format)}</strong>
          </div>
        ))}
      </div>
    </div>
  );
}

function TableSpec({ spec }) {
  return (
    <div className="eden-chat-viz__table-wrap">
      <table className="eden-chat-viz__table">
        <thead>
          <tr>
            {spec.columns.map((column) => (
              <th key={column.key}>{column.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {spec.data.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {spec.columns.map((column) => (
                <td key={column.key}>{formatValue(row[column.key], column.format)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function VisualizationBody({ spec }) {
  if (spec.status !== "ok") {
    return (
      <div className="eden-chat-viz__callout" role="note">
        {spec.reason || spec.description || "This visualization is not available for the current answer."}
      </div>
    );
  }

  if (spec.type === "line_chart") return <LineChartSpec spec={spec} />;
  if (spec.type === "bar_chart") return <BarChartSpec spec={spec} />;
  if (spec.type === "pie_chart") return <PieChartSpec spec={spec} />;
  if (spec.type === "table") return <TableSpec spec={spec} />;
  return null;
}

function VisualizationCard({ spec }) {
  return (
    <section className="eden-chat-viz__card" aria-label={spec.title}>
      <div className="eden-chat-viz__header">
        <h3>{spec.title}</h3>
        <p>{spec.description}</p>
      </div>
      <div className="eden-chat-viz__chart-shell">
        <VisualizationBody spec={spec} />
      </div>
    </section>
  );
}

export default function EdenChatVisualizationRenderer({ visualizations = [] }) {
  const safeVisualizations = useMemo(
    () => normalizeEdenVisualizations(visualizations),
    [visualizations]
  );

  if (!safeVisualizations.length) return null;

  return (
    <div className="eden-chat-viz" data-testid="eden-chat-visualizations">
      {safeVisualizations.map((spec, index) => (
        <VisualizationCard spec={spec} key={`${spec.type}-${spec.title}-${index}`} />
      ))}
    </div>
  );
}

export { formatValue };
